import AttemptAuthorization from '@/models/AttemptAuthorization.js';
import Model from '@/models/Model.js';
import Remediation from '@/models/Remediation.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default class ProctorDashboardControlPanel extends Model {
    static entity = 'proctor_dashboard';

    static state() {
        return {
            active: false,
            search_filter: null,
            is_loading: false,
            connection_status: 'connected',
            campus_filters: [
                {
                    name: 'orange-county',
                    active: false,
                },
                {
                    name: 'riverside',
                    active: false,
                },
                {
                    name: 'los-angeles',
                    active: false,
                },
                {
                    name: 'san-diego',
                    active: false,
                },
            ],
        };
    }

    static fields() {
        return {
            model: this.attr(null),
            user: this.attr(null),
            remediation_id: this.attr(null),
            remediation: this.belongsTo(Remediation, 'remediation_id', 'id'),
            attempt_authorization_id: this.attr(null),
            attempt_authorization: this.belongsTo(AttemptAuthorization, 'attempt_authorization_id', 'id'),
        };
    }

    get remediationId() {
        return this.remediation_id;
    }

    get queryParams() {
        return this.searchFilter;
    }

    reset() {}

    async updateStatusToWaitingRoom(obj) {
        if (obj) {
            await obj.updateStatusToWaitingRoom();
        }
    }

    async placeIntoWaitingRoom() {
        if (this.remediation) {
            await Remediation.query()
                .where('id', this.remediation_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .updateStatusToWaitingRoom();
        }

        if (this.attempt_authorization) {
            await AttemptAuthorization.query()
                .where('id', this.attempt_authorization_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .updateStatusToWaitingRoom();
        }
    }

    async startQuiz() {
        if (this.remediation) {
            await Remediation.query()
                .where('id', this.remediation_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .startQuiz();
        }

        if (this.attempt_authorization) {
            await AttemptAuthorization.query()
                .where('id', this.attempt_authorization_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .startQuiz();
        }
    }

    async waitingRoomEnd() {
        if (this.remediation) {
            await Remediation.query()
                .where('id', this.remediation_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .waitingRoomEnd();
        }

        if (this.attempt_authorization) {
            await AttemptAuthorization.query()
                .where('id', this.attempt_authorization_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .waitingRoomEnd();
        }
    }

    async inReview() {
        if (this.remediation) {
            await Remediation.query()
                .where('id', this.remediation_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .inReview();
        }

        if (this.attempt_authorization) {
            await AttemptAuthorization.query()
                .where('id', this.attempt_authorization_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .inReview();
        }
    }

    async endQuiz() {
        if (this.remediation) {
            await Remediation.query()
                .where('id', this.remediation_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .endQuiz();
        }

        if (this.attempt_authorization) {
            await AttemptAuthorization.query()
                .where('id', this.attempt_authorization_id)
                .with('activity.contentable')
                .with('user')
                .first()
                .endQuiz();
        }
    }

    static async fetch(params = null) {
        const queryBuilder = new QueryBuilder('proctor-dashboard').pageSize(20);

        if (params) {
            const { available_items, in_progress_items, finished_items, page, search_filters, campus_filters } = params;

            // Apply all item type flags at once
            queryBuilder.raw({
                ...(available_items && { available_items: true }),
                ...(in_progress_items && { in_progress_items: true }),
                ...(finished_items && { finished_items: true }),
            });

            if (page) queryBuilder.page(page);

            // Improved search filter handling with trimming and minimum length check
            if (search_filters) {
                const trimmedSearch = search_filters.trim();
                if (trimmedSearch.length >= 1) {
                    // Allow single character searches
                    queryBuilder.search(trimmedSearch);
                }
            }

            // Use passed campus_filters if available, otherwise use default campus filters
            // This ensures campus filtering always works even if not explicitly provided
            const filtersToUse = campus_filters || this.state().campus_filters;
            if (filtersToUse && Array.isArray(filtersToUse)) {
                filtersToUse
                    .filter(campusFilter => campusFilter.active)
                    .forEach(filter => queryBuilder.filter(filter.name, true));
            }
        }

        return axios.get(queryBuilder.toUrl());
    }

    /**
     * Callers decide when to insert (e.g. after checking a staleness guard), since `fetch()`
     * itself must not write to the shared store for a response a caller has already discarded.
     */
    static insertResponseItems(data) {
        ['available_items', 'in_progress_items', 'finished_items'].forEach(key => insertItems(data[key]));
    }
}

function insertItems(items) {
    if (items.length > 0) {
        items.forEach(item => {
            if (item.model_type === 'remediation') {
                Remediation.insert({ data: item, insert: ['users'] });
            }

            if (item.model_type === 'attempt_authorization') {
                AttemptAuthorization.insert({ data: item, insert: ['courses', 'activities.contentable', 'users'] });
            }
        });
    }
}
